import {
  type AfterViewInit,
  Component,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash';
import { defaultIfEmpty, forkJoin, Subscription } from 'rxjs';
import { generateIdFromOrgId } from '../../../server/services/utils.service';
import type { OrgTag } from '../../../server/types/org-types';
import type { OrgTaskFrontEnd } from '../../../server/types/task-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { TagService } from '../tag.service';
import {
  type ImportBulkTask,
  type TaskExportDownload,
  TaskService,
} from '../task.service';

@Component({
  selector: 'app-import-tasks',
  templateUrl: './import-tasks.component.html',
  styleUrls: ['./import-tasks.component.scss'],
  standalone: false,
})
export class ImportTasksComponent implements OnInit, AfterViewInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<ImportTasksComponent>);
  private readonly data: {
    spaceId: string;
    orgId: string;
  } = inject(MAT_DIALOG_DATA);

  private authUserService = inject(AuthUserService);
  private snackBar = inject(MatSnackBar);
  private taskService = inject(TaskService);
  private userService = inject(AuthUserService);
  private tagService = inject(TagService);

  @ViewChild('uploadImportTasks')
  uploadImportTasks: ElementRef<HTMLTextAreaElement>;

  authUserId: string;

  importUsers: {
    _id: string;
    name: string;
    taskCount: number;
    selected?: string;
  }[];
  spaceUsers: DeepReadonly<{ _id: string; name: string }>[];

  importTags: {
    _id: string;
    name: string;
    taskCount: number;
    selected?: string;
  }[];

  subscriptions = new Subscription();

  tasks: OrgTaskFrontEnd[] = [];
  tags: OrgTag[] = [];

  showFileInput = true;

  ngOnInit(): void {
    const { orgId, spaceId } = this.data;

    this.authUserId = this.authUserService.getAuthUserId({ orgId });

    this.spaceUsers = this.userService.getSpaceUserData({ orgId, spaceId });
    this.spaceUsers.push({ _id: 'unassigned', name: 'Unassigned' });
  }

  ngAfterViewInit(): void {
    this.uploadImportTasks.nativeElement.click();
  }

  onImportFileSelected(event) {
    const file: File = event.target.files[0];
    if (!file) return this.closeDialog();

    const reader = new FileReader();
    reader.readAsText(file, 'UTF-8');
    reader.onload = (event2) => {
      const contents = event2.target?.result;
      if (!contents || typeof contents !== 'string') {
        this.displayErrorLoadingFile("typeof contents !== 'string'");
        return;
      }
      try {
        const taskExportDownload: TaskExportDownload = JSON.parse(contents);
        const { type, tasks, users, tags } = taskExportDownload;

        if (type !== 'fociva-export v0.0.2') {
          this.displayErrorLoadingFile("type !== 'fociva-export v0.0.2'");
          return;
        }
        if (!users)
          // TODO: validate users and tasks
          throw new Error('users not found in import');

        this.tasks = tasks;
        if (tags) this.tags = tags;

        this.importUsers = users.map((u) => ({ ...u, taskCount: 0 }));
        this.importUsers.push({
          _id: 'unassigned',
          name: 'Unassigned',
          taskCount: 0,
          selected: 'unassigned',
        });

        tasks.forEach((task) => {
          const _id = task.assignedTo ?? 'unassigned';
          const found = this.importUsers.find((u) => u._id === _id);
          if (!found) console.error('userId not found', _id);
          else found.taskCount++;
        });

        this.showFileInput = false;
      } catch (err) {
        this.displayErrorLoadingFile(err);
      }
    };
    reader.onerror = (event3) => {
      this.displayErrorLoadingFile(event3);
    };
  }

  displayErrorLoadingFile(err) {
    this.snackBar.open('Error reading file');
  }

  runImportTasks() {
    const { orgId, spaceId } = this.data;

    const existingTags = this.tagService.getAllTagsSync({ orgId, spaceId });

    const sources = this.tags
      .filter((tag) => !existingTags.find((e) => e.name === tag.name.trim()))
      .map((tag) => {
        return this.tagService.createTag({ orgId, spaceId, name: tag.name });
      });

    const tagMapSub = forkJoin(sources)
      .pipe(defaultIfEmpty([]))
      .subscribe({
        next: (tagResult) => {
          const existingTags2 = this.tagService.getAllTagsSync({
            orgId,
            spaceId,
          });
          const tagMap = this.tags.map((tag) => {
            const existing = existingTags2.find(
              (e) => e.name === tag.name.trim()
            );
            if (!existing) throw new Error('Tag with matching name not found.');
            return {
              oldId: tag._id,
              newId: existing._id,
            };
          });

          const tasks = this.tasks.map((t) => {
            const {
              _id,
              tagIds: oldTagIds,
              assignedTo,
              weight,
              parentId,
              parentWeight,
              timeEstimate,
              schedule,
              dueAt,
              currentAt,
              completedAt,
              title,
              description,
              createdBy,
              createdAt,
              updatedAt,
              localAt,
              trashedAt,
              deletedAt,
            } = t;

            const tagIds: string[] = [];
            oldTagIds.forEach((old) => {
              const match = tagMap.find((m) => old === m.oldId);
              if (match) tagIds.push(match.newId);
            });

            const newTask2: ImportBulkTask = _.cloneDeep({
              _id,
              spaceId,
              salt: '',
              tagIds,
              assignedTo,
              weight,
              parentId,
              parentWeight,
              timeEstimate,
              schedule,
              dueAt,
              currentAt,
              completedAt,
              title,
              description,
              createdBy,
              createdAt,
              updatedAt,
              localAt,
              trashedAt,
              deletedAt,
              files: [],
            });
            return newTask2;
          });

          // Clear out missing parentIds
          const ids = tasks.map(({ _id }) => _id);
          tasks.forEach((t) => {
            if (t.parentId && !ids.includes(t.parentId)) t.parentId = undefined;
          });

          // Generate new ids
          tasks.forEach((t) => {
            const oldTaskId = t._id;
            const { _id, salt } = generateIdFromOrgId(orgId);
            t._id = _id;
            t.salt = salt;

            // Update parentIds
            tasks.forEach((t2) => {
              if (t2.parentId === oldTaskId) t2.parentId = _id;
            });
          });

          // Translate createdBy
          tasks.forEach((t) => {
            // Check for deleted tasks
            if (t.createdBy === '')
              if (t.deletedAt) return (t.createdBy = '');
              else throw new Error("Tasks createdBy set to '' but not deleted");

            const createdByImportUser = this.importUsers.find(
              (u) => u._id === t.createdBy
            );
            if (!createdByImportUser)
              throw new Error('createdBy userId not found'); // This should never fire
            const selected = createdByImportUser.selected;
            if (!selected) throw new Error('createdBy userId not selected'); // This should never fire
            t.createdBy =
              selected === 'unassigned' ? this.authUserId : selected; // default to self
          });

          // Translate assignedTo
          tasks.forEach((t) => {
            const oldAssignedTo = t.assignedTo ?? 'unassigned';
            const assignedToImportUser = this.importUsers.find(
              (u) => u._id === oldAssignedTo
            );
            if (!assignedToImportUser)
              throw new Error('assignedTo import userId not found'); // This should never fire
            const selected = assignedToImportUser.selected;
            if (!selected) throw new Error('assignedTo userId not selected'); // This should never fire
            t.assignedTo = selected === 'unassigned' ? undefined : selected;
          });

          this.taskService.bulkCreate({ orgId, spaceId, tasks }).subscribe({
            next: () => this.closeDialog(),
            error: (err) => this.displayErrorLoadingFile(err),
          });
        },
        error: () => {
          this.snackBar.open(
            'Problem creating tags. Please try again later',
            'Dismiss'
          );
        },
      });

    this.subscriptions.add(tagMapSub);
  }

  closeDialog(): void {
    this.dialogRef.close('Edit title dialog closed');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
